import React from 'react';
import { useWindowDimensions } from 'react-native';
import {
  BREAKPOINTS,
  Button,
  IllusFoodOnItsWay,
  useResponsive,
} from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { FeaturedChannel as OptimizedFeaturedChannel } from '../FeaturedChannel';
import { FeaturedChannel } from '../FeaturedChannels.components';

export const SecondaryChannel = (props: SecondaryChannelProps) => {
  const { t } = useLocalizationContext();
  const { minWidth } = useResponsive();
  const { titleCopy, buttonCopy } = useSecondaryCopy();

  const shouldUseOptimizedFeatureChannel = useFeatureFlag(
    'CELS-952-short-homescreen-optimizations-enabled',
  );

  if (shouldUseOptimizedFeatureChannel) {
    return (
      <OptimizedFeaturedChannel
        theme="quinoa"
        label={t('featured-channel.secondary.optimized.label')}
        title={t('featured-channel.secondary.optimized.title')}
        action={t('featured-channel.secondary.button-medium')}
        isLoading={false}
        onPress={props.onSeeMoreLocations}
      />
    );
  }

  return (
    <FeaturedChannel testID="featured-channel.secondary.card">
      <FeaturedChannel.Illustration source={IllusFoodOnItsWay} />
      <FeaturedChannel.TextContainer>
        <FeaturedChannel.Title>{titleCopy}</FeaturedChannel.Title>
      </FeaturedChannel.TextContainer>
      <FeaturedChannel.ButtonWrapper>
        <Button
          testID="featured-channel.secondary.button"
          palette="secondary"
          onPress={props.onSeeMoreLocations}
          size={minWidth.isMD ? 'large' : 'medium'}
        >
          {buttonCopy}
        </Button>
      </FeaturedChannel.ButtonWrapper>
    </FeaturedChannel>
  );
};

// ────────────────── Hooks ──────────────────

const useSecondaryCopy = () => {
  const { t } = useLocalizationContext();
  const { width } = useWindowDimensions();

  return {
    titleCopy: t('featured-channel.secondary.title'),
    buttonCopy:
      width > BREAKPOINTS[1].MAX + BREAKPOINTS[1].MARGIN
        ? t('featured-channel.secondary.button-large')
        : t('featured-channel.secondary.button-medium'),
  };
};

// ────────────────── Types ──────────────────

type SecondaryChannelProps = {
  readonly onSeeMoreLocations: () => void;
};
